import { Platform } from "react-native";

/**
 * MetaPixelManager
 * Manages Meta (Facebook) Pixel for sessions that start with onboarding.
 *
 * Design principles (matching ClarityManager):
 * - Only tracks sessions that BEGIN with onboarding (new user signup)
 * - Does NOT use persistent storage (no localStorage, no cookies before consent)
 * - Does NOT initialize Meta Pixel until user accepts cookies
 * - Only sends data to Meta servers when user accepts cookies
 * - Continues tracking even AFTER onboarding completes (throughout the session)
 * - If user reloads page, tracking stops (session-only, no persistence)
 * - Session-only tracking - no data persisted across page reloads
 */
class MetaPixelManager {
  constructor() {
    // 🚫 TEMPORARY DISABLE - Set to false when ready to enable Meta Pixel
    this.DISABLED = true;

    this.isInitialized = false;
    this.isTracking = false;
    this.pixelId = null;
    this.hasConsent = null; // null = not decided, true = accepted, false = rejected
    this.pendingEvents = []; // Store events until consent is granted
    this.fbq = null; // Reference to the fbq function
  }

  /**
   * Prepare Meta Pixel for initialization (but don't start yet)
   * Store the pixel ID and user context, but wait for consent
   * @param {string} pixelId - Your Meta Pixel ID
   */
  init(pixelId) {
    if (this.DISABLED) return;

    // Only works on web
    if (Platform.OS !== "web") {
      console.log(
        "[MetaPixelManager] Not on web platform, skipping initialization"
      );
      return;
    }

    if (!pixelId || pixelId === "YOUR_META_PIXEL_ID") {
      console.warn("[MetaPixelManager] Invalid or placeholder pixel ID");
      return;
    }

    // Store pixel ID for later initialization
    this.pixelId = pixelId;

    console.log("[MetaPixelManager] Ready to initialize (waiting for consent)");
  }

  /**
   * Inject Meta Pixel script into the page
   * This is called after consent is granted
   */
  _injectPixelScript() {
    if (typeof window === "undefined") return;

    // Check if fbq already exists (shouldn't happen, but safety check)
    if (window.fbq) {
      console.log("[MetaPixelManager] Meta Pixel already exists");
      this.fbq = window.fbq;
      return;
    }

    // Standard Meta Pixel code
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );

    this.fbq = window.fbq;
  }

  /**
   * Grant consent and initialize Meta Pixel
   * This should be called when user clicks "Accept all"
   * This is when Meta Pixel actually initializes and starts tracking
   */
  grantConsent() {
    if (this.DISABLED) return;

    if (!this.pixelId) {
      console.warn(
        "[MetaPixelManager] Cannot grant consent - not prepared (no pixel ID)"
      );
      return;
    }

    if (this.isInitialized) {
      console.log("[MetaPixelManager] Already initialized with consent");
      return;
    }

    try {
      // Inject the Meta Pixel script
      this._injectPixelScript();

      if (!this.fbq) {
        console.error("[MetaPixelManager] Failed to inject Meta Pixel script");
        return;
      }

      // Initialize the pixel
      this.fbq("init", this.pixelId, {
        em: null, // Can be set later with user email if needed
      });

      // Grant consent for data processing
      this.fbq("consent", "grant");

      this.isInitialized = true;
      this.isTracking = true;
      this.hasConsent = true;

      console.log(
        "[MetaPixelManager] Consent granted - Meta Pixel initialized and tracking started"
      );

      // Track PageView (initial event)
      this.fbq("track", "PageView");

      // Replay all pending events that happened before consent
      this.pendingEvents.forEach((event) => {
        if (event.type === "standard") {
          this.fbq("track", event.name, event.params);
        } else if (event.type === "custom") {
          this.fbq("trackCustom", event.name, event.params);
        }
      });
      this.pendingEvents = [];

      console.log(
        `[MetaPixelManager] Replayed ${this.pendingEvents.length} pending events`
      );
    } catch (error) {
      console.error("[MetaPixelManager] Failed to grant consent:", error);
    }
  }

  /**
   * Deny consent and stop tracking
   * This should be called when user clicks "Reject all"
   * Meta Pixel will never be initialized, so no data is collected or sent
   */
  denyConsent() {
    if (this.DISABLED) return;

    if (this.isInitialized) {
      console.warn(
        "[MetaPixelManager] Meta Pixel was already initialized - revoking consent"
      );
      // Revoke consent if already initialized
      if (this.fbq) {
        this.fbq("consent", "revoke");
      }
      this.isTracking = false;
      return;
    }

    this.hasConsent = false;
    this.isTracking = false;

    console.log(
      "[MetaPixelManager] Consent denied - Meta Pixel will NOT be initialized"
    );

    // Clear any pending data
    this.pendingEvents = [];
    this.pixelId = null;
  }

  /**
   * Track a standard event
   * If consent not granted yet, event is queued
   * @param {string} eventName - Standard event name (e.g., 'CompleteRegistration', 'Lead', 'Purchase')
   * @param {object} params - Optional event parameters
   */
  trackEvent(eventName, params = {}) {
    if (this.DISABLED) return;

    if (!this.isInitialized || !this.fbq) {
      // Queue event for later if consent not granted yet
      this.pendingEvents.push({ type: "standard", name: eventName, params });
      console.log(
        `[MetaPixelManager] Event "${eventName}" queued (waiting for consent)`
      );
      return;
    }

    if (!this.isTracking) {
      console.log(
        `[MetaPixelManager] Not tracking - event "${eventName}" ignored`
      );
      return;
    }

    try {
      this.fbq("track", eventName, params);
      console.log(`[MetaPixelManager] Tracked event: ${eventName}`, params);
    } catch (error) {
      console.error(
        `[MetaPixelManager] Failed to track event "${eventName}":`,
        error
      );
    }
  }

  /**
   * Track a custom event
   * If consent not granted yet, event is queued
   * @param {string} eventName - Custom event name
   * @param {object} params - Optional event parameters
   */
  trackCustomEvent(eventName, params = {}) {
    if (this.DISABLED) return;

    if (!this.isInitialized || !this.fbq) {
      // Queue event for later if consent not granted yet
      this.pendingEvents.push({ type: "custom", name: eventName, params });
      console.log(
        `[MetaPixelManager] Custom event "${eventName}" queued (waiting for consent)`
      );
      return;
    }

    if (!this.isTracking) {
      console.log(
        `[MetaPixelManager] Not tracking - custom event "${eventName}" ignored`
      );
      return;
    }

    try {
      this.fbq("trackCustom", eventName, params);
      console.log(
        `[MetaPixelManager] Tracked custom event: ${eventName}`,
        params
      );
    } catch (error) {
      console.error(
        `[MetaPixelManager] Failed to track custom event "${eventName}":`,
        error
      );
    }
  }

  /**
   * Set user data for advanced matching
   * @param {object} userData - User data object (email, phone, etc.)
   * Example: { em: 'user@example.com', ph: '1234567890' }
   */
  setUserData(userData) {
    if (this.DISABLED) return;

    if (!this.isInitialized || !this.fbq) {
      console.log(
        "[MetaPixelManager] User data not set - pixel not initialized"
      );
      return;
    }

    try {
      // Hash sensitive data before sending (Meta recommends SHA-256)
      // For now, we'll let Meta handle hashing
      this.fbq("init", this.pixelId, userData);
      console.log("[MetaPixelManager] User data updated");
    } catch (error) {
      console.error("[MetaPixelManager] Failed to set user data:", error);
    }
  }

  /**
   * Stop tracking completely
   * Call this manually if you need to stop tracking mid-session
   * Note: Normally, tracking continues for the entire browser session
   */
  stop() {
    if (this.DISABLED) return;

    if (!this.isTracking) {
      return;
    }

    this.isTracking = false;
    console.log("[MetaPixelManager] Stopped tracking (manual stop called)");

    // Revoke consent if pixel is initialized
    if (this.fbq) {
      this.fbq("consent", "revoke");
    }
  }

  /**
   * Get initialization status
   * @returns {boolean} True if pixel is initialized and tracking
   */
  isActive() {
    return this.isInitialized && this.isTracking;
  }
}

// Export singleton instance
export default new MetaPixelManager();
